import {
  createContext,
  type MouseEventHandler,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { IconClose } from '../icons/IconClose';
import { classNames } from '../util';
import styles from './index.module.css';

// TODO: this whole file is a mess, let's find a better way to combine react + dialog + animations
interface DialogProps {
  title: string;
  className?: string;
  onClose?: () => void;
  isDrawer?: boolean;
}

const dialogObserver = new MutationObserver((mutations) => {
  mutations.forEach(async (mutation) => {
    if (mutation.attributeName === 'open') {
      const dialog = mutation.target as HTMLDialogElement;
      const isOpen = dialog.hasAttribute('open');

      if (isOpen) {
        dialog.removeAttribute('inert');
      } else {
        dialog.setAttribute('inert', 'true');
      }
    }
  });
});

const useDialog = () => {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (!dialogRef.current) return;

    dialogObserver.observe(dialogRef.current, { attributes: true });
  }, []);

  const open = useCallback(() => {
    dialogRef.current?.showModal();
    dialogRef.current?.removeAttribute('inert');
  }, []);
  const close = useCallback(() => {
    dialogRef.current?.close();
    dialogRef.current?.setAttribute('inert', 'true');
  }, []);

  const Dialog = useCallback(
    ({
      title,
      children,
      className = '',
      onClose,
      isDrawer = false,
    }: PropsWithChildren<DialogProps>) => {
      const closeFn = onClose ?? close;

      // biome-ignore lint/correctness/useHookAtTopLevel: biome doesn't recognize that this is a nested React component (or maybe nested React components are a bad idea)
      const handleBackdropClick = useCallback<MouseEventHandler<HTMLDialogElement>>(
        (e) => {
          if (e.target !== dialogRef.current) return;

          closeFn();
        },
        [closeFn],
      );

      return (
        <dialog
          className={styles.backdrop}
          // @ts-expect-error -- TS doesn't recognize the inert prop
          inert="true"
          onClick={handleBackdropClick}
          ref={dialogRef}
        >
          <div
            className={classNames(styles.dialog, className, 'parchment', 'overlay', {
              [styles.drawer]: isDrawer,
              [styles.modal]: !isDrawer,
            })}
          >
            <header>
              <h3>{title}</h3>
              <button
                type="button"
                className="secondary"
                /** biome-ignore lint/a11y/noAutofocus: autoFocus is fine and needed on dialogs */
                autoFocus
                aria-label="Close"
                onClick={closeFn}
              >
                <IconClose />
              </button>
            </header>
            {children}
          </div>
        </dialog>
      );
    },
    [close],
  );

  return useMemo(() => [Dialog, { open, close }] as const, [Dialog, open, close]);
};

type DialogPropsWithChildren = PropsWithChildren<DialogProps>;

interface DialogContextValue {
  open: (options: DialogPropsWithChildren) => void;
  close: () => void;
}

const DialogContext = createContext<DialogContextValue>({
  open: () => {},
  close: () => {},
});

export const DialogProvider = ({ children }: PropsWithChildren) => {
  const [Dialog, { open: openDialog, close: closeDialog }] = useDialog();
  const [dialogProps, setDialogProps] = useState<DialogPropsWithChildren & { isOpen: boolean }>({
    title: '',
    isOpen: false,
  });
  const open = useCallback((options: DialogPropsWithChildren) => {
    setDialogProps({ ...options, isOpen: true });
  }, []);

  const close = useCallback(() => {
    setDialogProps((prevProps) => ({ ...prevProps, isOpen: false }));
  }, []);

  useEffect(() => {
    if (dialogProps.isOpen) openDialog();
    else closeDialog();
  }, [dialogProps.isOpen, openDialog, closeDialog]);

  useEffect(() => {
    const escHandler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;

      close();
    };

    document.addEventListener('keyup', escHandler);

    return () => document.removeEventListener('keyup', escHandler);
  }, [close]);

  const value = useMemo(() => ({ open, close }), [open, close]);

  return (
    <DialogContext.Provider value={value}>
      {children}
      <Dialog {...dialogProps} onClose={close} />
    </DialogContext.Provider>
  );
};

export const useSingleDialog = () => useContext(DialogContext);
